import { getAccessToken, onAuthLogout, onTokenChange, refreshAccessToken } from '@/shared/api/client';
import { withApiBase } from '@/shared/config/constants';

export type RealtimeEvent = {
  id: string;
  scope: string;
  scopeId: string;
  type: string;
  entityType: string;
  entityId: string;
  createdAt: string;
  payload: Record<string, unknown>;
};

type SubscriptionParams = {
  scope: string;
  id: string;
  onEvent: (event: RealtimeEvent) => void | Promise<void>;
  onReconnect?: () => void | Promise<void>;
};

type ChannelState = {
  source: EventSource;
  listeners: Map<string, SubscriptionParams>;
  connectedOnce: boolean;
  scope: string;
  id: string;
};

export class RealtimeClient {
  private channels = new Map<string, ChannelState>();
  private unsubLogout: (() => void) | null = null;
  private unsubTokenChange: (() => void) | null = null;
  private reconnecting = false;

  constructor() {
    this.unsubLogout = onAuthLogout(() => {
      this.closeAll();
    });
    this.unsubTokenChange = onTokenChange(() => {
      if (this.channels.size > 0) this.reconnectAll();
    });
  }

  subscribe(params: SubscriptionParams) {
    const key = `${params.scope}:${params.id}`;
    const listenerId = `${key}:${Math.random().toString(36).slice(2)}`;
    const channel = this.ensureChannel(params.scope, params.id);
    channel.listeners.set(listenerId, params);
    return listenerId;
  }

  unsubscribe(listenerId: string) {
    for (const [channelKey, channel] of this.channels.entries()) {
      if (!channel.listeners.has(listenerId)) continue;
      channel.listeners.delete(listenerId);
      if (!channel.listeners.size) {
        channel.source.close();
        this.channels.delete(channelKey);
      }
      return;
    }
  }

  closeAll() {
    for (const [, channel] of this.channels) {
      channel.source.close();
    }
    this.channels.clear();
  }

  reconnectAll() {
    if (this.reconnecting) return;
    this.reconnecting = true;
    // Snapshot current channels, then clear — avoids mutating Map during iteration
    const snapshot = Array.from(this.channels.entries());
    for (const [, channel] of snapshot) {
      channel.source.close();
    }
    this.channels.clear();
    for (const [, oldChannel] of snapshot) {
      const newChannel = this.ensureChannel(oldChannel.scope, oldChannel.id);
      for (const [lid, params] of oldChannel.listeners) {
        newChannel.listeners.set(lid, params);
      }
    }
    this.reconnecting = false;
  }

  destroy() {
    this.closeAll();
    this.unsubLogout?.();
    this.unsubTokenChange?.();
  }

  private ensureChannel(scope: string, id: string) {
    const channelKey = `${scope}:${id}`;
    const current = this.channels.get(channelKey);
    if (current) return current;

    const token = getAccessToken();
    const searchParams = new URLSearchParams({ scope, id, token: token ?? '' });
    const source = new EventSource(withApiBase(`/api/events?${searchParams.toString()}`));
    const state: ChannelState = { source, listeners: new Map(), connectedOnce: false, scope, id };

    source.addEventListener('rt-event', (rawEvent) => {
      const event = JSON.parse((rawEvent as MessageEvent<string>).data) as RealtimeEvent;
      state.listeners.forEach((listener) => {
        void listener.onEvent(event);
      });
    });

    source.addEventListener('ping', () => {
      if (state.connectedOnce) return;
      state.connectedOnce = true;
    });

    source.onopen = () => {
      if (!state.connectedOnce) {
        state.connectedOnce = true;
        return;
      }
      state.listeners.forEach((listener) => {
        void listener.onReconnect?.();
      });
    };

    let errorCount = 0;
    let recovering = false;
    source.onerror = () => {
      errorCount++;
      if (errorCount < 3 || recovering) return;
      recovering = true;
      // Close immediately to stop browser auto-reconnect with stale token
      source.close();
      this.channels.delete(channelKey);
      // Try to refresh token, then reconnect with fresh token
      void refreshAccessToken().finally(() => {
        setTimeout(() => {
          recovering = false;
          if (getAccessToken()) {
            const rebuilt = this.ensureChannel(scope, id);
            for (const [lid, params] of state.listeners) {
              rebuilt.listeners.set(lid, params);
            }
          }
        }, 3000);
      });
    };

    this.channels.set(channelKey, state);
    return state;
  }
}
